import api from './api';
import { SpreadConfig, SpreadMarket, SpreadDirection } from '../types/spread';

// API client for the NY Spread / SX Spread feature. Kept in its own file so the
// existing services/api.ts (NSE/BSE) is never touched.

const marketPath = (market: SpreadMarket) => (market === 'NIFTY' ? 'nifty' : 'sensex');

export const spreadConfigAPI = {
  getConfigs: (market: SpreadMarket) => api.get(`/spread/${marketPath(market)}/config`),
  createConfig: (market: SpreadMarket, config: Partial<SpreadConfig>) => api.post(`/spread/${marketPath(market)}/config`, config),
  updateConfig: (market: SpreadMarket, id: number, config: Partial<SpreadConfig>) => api.put(`/spread/${marketPath(market)}/config/${id}`, config),
  duplicateConfig: (market: SpreadMarket, id: number) => api.post(`/spread/${marketPath(market)}/config/${id}/duplicate`),
  toggleActive: (market: SpreadMarket, id: number, is_active: boolean) => api.put(`/spread/${marketPath(market)}/config/${id}`, { is_active }),
  deleteConfig: (market: SpreadMarket, id: number) => api.delete(`/spread/${marketPath(market)}/config/${id}`),
  bulkEnable: (market: SpreadMarket, ids: number[]) => api.put(`/spread/${marketPath(market)}/config/bulk-enable`, { ids }),
  // Manual "instant order" — fires entry/exit immediately (expiry+lot from the config,
  // skips the X/Y signal wait and, for exit, the SL/Target/TSL check).
  instantEntry: (market: SpreadMarket, id: number) => api.post(`/spread/${marketPath(market)}/config/${id}/instant-entry`),
  instantExit: (market: SpreadMarket, id: number) => api.post(`/spread/${marketPath(market)}/config/${id}/instant-exit`),
  // "Manual Spread" page — one-shot trade, no saved config list. Creates a config + fires
  // entry in a single server-side request.
  manualPlace: (market: SpreadMarket, body: {
    direction: SpreadDirection; option_type: 'CE' | 'PE'; expiry_date?: string;
    first_leg_offset: number; second_leg_offset: number; capital_percentage: number;
    slippage_percentage?: number; delay_seconds?: number; third_leg_enabled?: boolean;
    sl_offset?: number; sl_points?: number; tsl_np_points?: number; broker?: string;
  }) => api.post(`/spread/${marketPath(market)}/config/manual-place`, body),
  // Per-(market, direction, option_type) saved defaults for the Manual Spread panel — UI
  // convenience only, pre-fills the panel's fields instead of its hardcoded ones.
  getManualDefaults: (market: SpreadMarket) => api.get<ManualSpreadDefault[]>(`/spread/${marketPath(market)}/config/manual-defaults`),
  saveManualDefault: (market: SpreadMarket, body: ManualSpreadDefaultInput) =>
    api.put(`/spread/${marketPath(market)}/config/manual-defaults`, body),
  clearManualDefault: (market: SpreadMarket, direction: SpreadDirection, option_type: 'CE' | 'PE') =>
    api.delete(`/spread/${marketPath(market)}/config/manual-defaults`, { params: { direction, option_type } }),
};

export interface ManualSpreadDefault {
  direction: SpreadDirection; option_type: 'CE' | 'PE';
  first_leg_offset: number; second_leg_offset: number; capital_percentage: number;
  slippage_percentage: number; delay_seconds: number;
  sl_offset: number | null; sl_points: number | null; tsl_np_points: number | null;
  third_leg_enabled: boolean;
  broker: string;
}
export type ManualSpreadDefaultInput = ManualSpreadDefault;

export const spreadOrdersAPI = {
  getOrders: (market: SpreadMarket, page = 1, limit = 50) => api.get(`/spread/${marketPath(market)}/orders?page=${page}&limit=${limit}`),
  getOpenOrders: (market: SpreadMarket) => api.get(`/spread/${marketPath(market)}/orders/open`),
  exitAll: (market: SpreadMarket) => api.post(`/spread/${marketPath(market)}/orders/exit-all`),
  clearHistory: (market: SpreadMarket) => api.delete(`/spread/${marketPath(market)}/orders/clear`),
  deletePermanently: (market: SpreadMarket) => api.delete(`/spread/${marketPath(market)}/orders/delete-permanently`),
};

export interface SpreadSettingRow {
  market: SpreadMarket;
  direction: SpreadDirection;
  is_enabled: boolean | number;
  default_delay_seconds: number;
}

export const spreadSettingsAPI = {
  getSettings: () => api.get<SpreadSettingRow[]>('/spread/settings'),
  updateSetting: (market: SpreadMarket, direction: SpreadDirection, is_enabled: boolean, default_delay_seconds?: number) =>
    api.put('/spread/settings', { market, direction, is_enabled, default_delay_seconds }),
};
